import { useState } from "react";
import { Share2, Copy, Check, Send, X, Mail, Building2, User as UserIcon, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export function ShareWithCoachModal({ athleteId, athleteName, open, onClose }) {
  const [coachEmail, setCoachEmail] = useState("");
  const [coachName, setCoachName] = useState("");
  const [coachSchool, setCoachSchool] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCoachEmail(""); setCoachName(""); setCoachSchool(""); setNote("");
    setResult(null); setCopied(false);
  };

  const close = () => { reset(); onClose && onClose(); };

  const handleGenerate = async () => {
    if (!coachEmail || !coachEmail.includes("@")) {
      toast.error("Please enter a valid coach email");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          coach_email: coachEmail.trim().toLowerCase(),
          coach_name: coachName.trim(),
          coach_school: coachSchool.trim(),
          note: note.trim(),
        }),
      });
      // Use .text() + JSON.parse to bypass platform fetch interceptor
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch { /* interceptor consumed body */ }
      if (!r.ok) throw new Error(data.detail || "Failed to generate share link");
      setResult(data);
      const emailOk = data.email && data.email.status === "success";
      toast.success(emailOk ? `Share link emailed to ${coachEmail}` : "Share link generated");
    } catch (e) {
      toast.error(e.message || "Failed to create share");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!result?.share_url) return;
    try {
      await navigator.clipboard.writeText(result.share_url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-coach-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <Share2 size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">Share with a Coach</h2>
              <p className="text-blue-100 text-xs">{athleteName ? `${athleteName}'s` : "Your"} private scouting report</p>
            </div>
          </div>
          <button onClick={close} className="text-white/70 hover:text-white" data-testid="share-coach-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {!result ? (
            <>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-5 flex gap-2">
                <AlertCircle size={16} className="text-blue-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-900 leading-relaxed">
                  This generates a <strong>private, single-recipient link</strong> giving this coach access to your full Scouting Report, Tool Grades, and Highlight Video. The link stays active until <strong>you revoke it</strong> from the Share Activity tab.
                </p>
              </div>

              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Coach Email *</label>
              <div className="relative mb-4">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email" required value={coachEmail} onChange={(e) => setCoachEmail(e.target.value)}
                  placeholder="coach@university.edu"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  data-testid="share-coach-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Coach Name</label>
                  <div className="relative">
                    <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text" value={coachName} onChange={(e) => setCoachName(e.target.value)}
                      placeholder="Coach Smith"
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                      data-testid="share-coach-name-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">School</label>
                  <div className="relative">
                    <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text" value={coachSchool} onChange={(e) => setCoachSchool(e.target.value)}
                      placeholder="LSU"
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                      data-testid="share-coach-school-input"
                    />
                  </div>
                </div>
              </div>

              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Personal Message (optional)</label>
              <textarea
                rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Hi Coach — I'd love your evaluation. Here's my full report..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none mb-6"
                data-testid="share-coach-note-input"
              />

              <Button
                onClick={handleGenerate} disabled={loading || !coachEmail}
                className="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold py-3"
                data-testid="share-coach-generate-btn"
              >
                {loading ? "Generating…" : (<><Send size={14} className="mr-2" /> Send Private Link to Coach</>)}
              </Button>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-green-50 border border-green-200 p-4 mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <Check size={16} className="text-green-700" />
                  <h3 className="text-sm font-bold text-green-900">Share link created</h3>
                </div>
                <p className="text-xs text-green-800">
                  {result.email && result.email.status === "success"
                    ? `Email sent to ${coachEmail}. They can also use the link below.`
                    : `Copy the link below and send it to the coach. (Email delivery isn't configured on this environment.)`}
                </p>
              </div>

              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Private Share Link</label>
              <div className="flex gap-2 mb-5">
                <input
                  readOnly value={result.share_url}
                  className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50"
                  data-testid="share-coach-result-url"
                />
                <Button onClick={copyLink} variant="outline" className="flex-shrink-0" data-testid="share-coach-copy-btn">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>

              <div className="text-xs text-gray-500 mb-4 space-y-1">
                <div>Recipient: <span className="font-semibold text-gray-900">{coachEmail}</span></div>
                <div>Status: <span className="font-semibold text-green-700">Active until you revoke it</span></div>
                <div>Share ID: <span className="font-mono text-gray-700">{result.share.share_id}</span></div>
              </div>

              <Button onClick={close} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="share-coach-done-btn">Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
